import {
  Area,
  AreaChart,
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import { GlassDonutChart } from "@/components/charts/GlassDonutChart";
import {
  AXIS_TICK,
  AXIS_TICK_BOLD,
  BAR_ANIMATION,
  CATEGORY_TICK,
  ChartGradientDefs,
  ChartTooltip,
  GRID_STROKE,
  LINE_ANIMATION,
} from "@/components/charts/chart-kit";
import type { FuelActivityCharts, UserFuelCharts } from "@/lib/fuel-analytics";
import { BRAND, CHART_COLORS } from "@/lib/brand-colors";
import { formatCurrency, formatFuelPrice, formatNumber } from "@/lib/utils";

function ChartPanel({
  title,
  subtitle,
  children,
  chartClassName = "h-[220px]",
}: {
  title: string;
  subtitle: string;
  children: React.ReactNode;
  chartClassName?: string;
}) {
  return (
    <div className="rounded-3xl bg-canvas p-4 sm:p-5">
      <p className="text-[10px] font-bold uppercase tracking-wider text-muted">{title}</p>
      <p className="mt-0.5 text-xs text-muted">{subtitle}</p>
      <div className={`mt-4 w-full ${chartClassName}`}>{children}</div>
    </div>
  );
}

function EmptyChart({ message }: { message: string }) {
  return <p className="flex h-full items-center justify-center text-sm text-muted">{message}</p>;
}

export function PlatformFuelCharts({ charts }: { charts: FuelActivityCharts }) {
  return (
    <div className="grid gap-4 lg:grid-cols-2">
      <ChartPanel title="Weekly spend" subtitle="Total top-up spend grouped by week">
        {charts.spendByWeek.length > 0 ? (
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={charts.spendByWeek} margin={{ top: 4, right: 4, left: -16, bottom: 0 }}>
              <ChartGradientDefs />
              <CartesianGrid strokeDasharray="3 3" stroke={GRID_STROKE} vertical={false} />
              <XAxis dataKey="name" tick={AXIS_TICK} axisLine={false} tickLine={false} />
              <YAxis tick={AXIS_TICK} axisLine={false} tickLine={false} />
              <Tooltip
                content={({ active, payload, label }) => (
                  <ChartTooltip
                    active={active}
                    payload={payload}
                    label={label}
                    valueLabel=""
                    formatValue={(value) => formatCurrency(value)}
                  />
                )}
                cursor={{ fill: BRAND.accentMuted, radius: 8 }}
              />
              <Bar
                dataKey="value"
                radius={[10, 10, 4, 4]}
                maxBarSize={48}
                fill="url(#gradAccentY)"
                {...BAR_ANIMATION}
              />
            </BarChart>
          </ResponsiveContainer>
        ) : (
          <EmptyChart message="No spend data in this period." />
        )}
      </ChartPanel>

      <ChartPanel title="Spend by grade" subtitle="How fuel spend splits across grades">
        {charts.spendByGrade.length > 0 ? (
          <GlassDonutChart data={charts.spendByGrade} valueLabel="JMD" maxSlices={6} minLabelPercent={0.03} />
        ) : (
          <EmptyChart message="No grade breakdown available." />
        )}
      </ChartPanel>

      <ChartPanel title="Fill-ups by weekday" subtitle="Most popular days for top-ups across all users (Mon–Sun)">
        {charts.fillUpsByDayOfWeek.some((entry) => entry.value > 0) ? (
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={charts.fillUpsByDayOfWeek} margin={{ top: 4, right: 4, left: -16, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke={GRID_STROKE} vertical={false} />
              <XAxis dataKey="name" tick={AXIS_TICK_BOLD} axisLine={false} tickLine={false} />
              <YAxis allowDecimals={false} tick={AXIS_TICK} axisLine={false} tickLine={false} />
              <Tooltip
                content={({ active, payload, label }) => (
                  <ChartTooltip active={active} payload={payload} label={label} valueLabel="fill-ups" />
                )}
                cursor={{ fill: BRAND.accentMuted, radius: 8 }}
              />
              <Bar dataKey="value" radius={[10, 10, 4, 4]} maxBarSize={48} {...BAR_ANIMATION}>
                {charts.fillUpsByDayOfWeek.map((entry, index) => (
                  <Cell key={index} fill={entry.fill ?? CHART_COLORS[index % CHART_COLORS.length]} />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        ) : (
          <EmptyChart message="No weekday fill-up data yet." />
        )}
      </ChartPanel>

      <ChartPanel title="Top users" subtitle="Highest spenders in the selected period">
        {charts.topUsersBySpend.length > 0 ? (
          <ResponsiveContainer width="100%" height="100%">
            <BarChart
              data={charts.topUsersBySpend}
              layout="vertical"
              margin={{ top: 4, right: 12, left: 4, bottom: 0 }}
            >
              <CartesianGrid strokeDasharray="3 3" stroke={GRID_STROKE} horizontal={false} />
              <XAxis type="number" hide />
              <YAxis
                type="category"
                dataKey="name"
                width={88}
                tick={CATEGORY_TICK}
                axisLine={false}
                tickLine={false}
              />
              <Tooltip
                content={({ active, payload, label }) => (
                  <ChartTooltip
                    active={active}
                    payload={payload}
                    label={label}
                    valueLabel=""
                    formatValue={(value) => formatCurrency(value)}
                  />
                )}
                cursor={{ fill: BRAND.accentMuted, radius: 8 }}
              />
              <Bar dataKey="value" radius={[6, 10, 10, 6]} maxBarSize={22} {...BAR_ANIMATION}>
                {charts.topUsersBySpend.map((_, index) => (
                  <Cell key={index} fill={CHART_COLORS[index % CHART_COLORS.length]} />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        ) : (
          <EmptyChart message="No user spend data yet." />
        )}
      </ChartPanel>

      <ChartPanel title="Fill-ups by month" subtitle="Volume of logged top-ups over time">
        {charts.fillUpsByMonth.length > 0 ? (
          <ResponsiveContainer width="100%" height="100%">
            <AreaChart data={charts.fillUpsByMonth} margin={{ top: 4, right: 8, left: -16, bottom: 0 }}>
              <ChartGradientDefs />
              <CartesianGrid strokeDasharray="3 3" stroke={GRID_STROKE} vertical={false} />
              <XAxis dataKey="name" tick={AXIS_TICK} axisLine={false} tickLine={false} />
              <YAxis allowDecimals={false} tick={AXIS_TICK} axisLine={false} tickLine={false} />
              <Tooltip
                content={({ active, payload, label }) => (
                  <ChartTooltip active={active} payload={payload} label={label} valueLabel="fill-ups" />
                )}
                cursor={{ stroke: BRAND.accent, strokeWidth: 1, strokeDasharray: "4 4" }}
              />
              <Area
                type="monotone"
                dataKey="value"
                stroke={BRAND.accent}
                strokeWidth={3}
                fill="url(#gradAccentArea)"
                dot={false}
                activeDot={{ r: 5, fill: BRAND.accent, stroke: "#fff", strokeWidth: 2 }}
                {...LINE_ANIMATION}
              />
            </AreaChart>
          </ResponsiveContainer>
        ) : (
          <EmptyChart message="No fill-up trend data yet." />
        )}
      </ChartPanel>
    </div>
  );
}

export function UserFuelCharts({ charts }: { charts: UserFuelCharts }) {
  return (
    <div className="grid gap-4 lg:grid-cols-2">
      <ChartPanel title="Spend timeline" subtitle="Weekly top-up spend for this user">
        {charts.spendTimeline.length > 0 ? (
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={charts.spendTimeline} margin={{ top: 4, right: 4, left: -16, bottom: 0 }}>
              <ChartGradientDefs />
              <CartesianGrid strokeDasharray="3 3" stroke={GRID_STROKE} vertical={false} />
              <XAxis dataKey="name" tick={AXIS_TICK} axisLine={false} tickLine={false} />
              <YAxis tick={AXIS_TICK} axisLine={false} tickLine={false} />
              <Tooltip
                content={({ active, payload, label }) => (
                  <ChartTooltip
                    active={active}
                    payload={payload}
                    label={label}
                    valueLabel=""
                    formatValue={(value) => formatCurrency(value)}
                  />
                )}
                cursor={{ fill: BRAND.accentMuted, radius: 8 }}
              />
              <Bar
                dataKey="value"
                radius={[10, 10, 4, 4]}
                maxBarSize={48}
                fill="url(#gradAccentY)"
                {...BAR_ANIMATION}
              />
            </BarChart>
          </ResponsiveContainer>
        ) : (
          <EmptyChart message="No spend timeline for this period." />
        )}
      </ChartPanel>

      <ChartPanel title="Litres over time" subtitle="Fuel volume logged each week">
        {charts.litresTimeline.length > 0 ? (
          <ResponsiveContainer width="100%" height="100%">
            <AreaChart data={charts.litresTimeline} margin={{ top: 4, right: 8, left: -16, bottom: 0 }}>
              <ChartGradientDefs />
              <CartesianGrid strokeDasharray="3 3" stroke={GRID_STROKE} vertical={false} />
              <XAxis dataKey="name" tick={AXIS_TICK} axisLine={false} tickLine={false} />
              <YAxis tick={AXIS_TICK} axisLine={false} tickLine={false} />
              <Tooltip
                content={({ active, payload, label }) => (
                  <ChartTooltip
                    active={active}
                    payload={payload}
                    label={label}
                    valueLabel="L"
                    formatValue={(value) => formatNumber(value)}
                    accentColor="#34c759"
                  />
                )}
                cursor={{ stroke: "#34c759", strokeWidth: 1, strokeDasharray: "4 4" }}
              />
              <Area
                type="monotone"
                dataKey="value"
                stroke="#34c759"
                strokeWidth={3}
                fill="url(#gradGreenArea)"
                dot={false}
                activeDot={{ r: 5, fill: "#34c759", stroke: "#fff", strokeWidth: 2 }}
                {...LINE_ANIMATION}
              />
            </AreaChart>
          </ResponsiveContainer>
        ) : (
          <EmptyChart message="No litres data for this period." />
        )}
      </ChartPanel>

      <ChartPanel title="Price per litre" subtitle="Average paid price trend by week">
        {charts.pricePerLitreTimeline.length > 0 ? (
          <ResponsiveContainer width="100%" height="100%">
            <AreaChart data={charts.pricePerLitreTimeline} margin={{ top: 4, right: 8, left: -16, bottom: 0 }}>
              <ChartGradientDefs />
              <CartesianGrid strokeDasharray="3 3" stroke={GRID_STROKE} vertical={false} />
              <XAxis dataKey="name" tick={AXIS_TICK} axisLine={false} tickLine={false} />
              <YAxis tick={AXIS_TICK} axisLine={false} tickLine={false} />
              <Tooltip
                content={({ active, payload, label }) => (
                  <ChartTooltip
                    active={active}
                    payload={payload}
                    label={label}
                    valueLabel="/L"
                    formatValue={(value) => formatFuelPrice(value)}
                    accentColor="#ff9500"
                  />
                )}
                cursor={{ stroke: "#ff9500", strokeWidth: 1, strokeDasharray: "4 4" }}
              />
              <Area
                type="monotone"
                dataKey="value"
                stroke="#ff9500"
                strokeWidth={3}
                fill="url(#gradOrangeArea)"
                dot={false}
                activeDot={{ r: 5, fill: "#ff9500", stroke: "#fff", strokeWidth: 2 }}
                {...LINE_ANIMATION}
              />
            </AreaChart>
          </ResponsiveContainer>
        ) : (
          <EmptyChart message="No price trend data." />
        )}
      </ChartPanel>

      <ChartPanel title="By vehicle" subtitle="Spend split across owned cars">
        {charts.spendByCar.length > 0 ? (
          <GlassDonutChart data={charts.spendByCar} valueLabel="JMD" maxSlices={5} minLabelPercent={0.04} />
        ) : (
          <EmptyChart message="No vehicle breakdown." />
        )}
      </ChartPanel>

      <ChartPanel title="By fuel grade" subtitle="Which grades this user buys most">
        {charts.spendByGrade.length > 0 ? (
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={charts.spendByGrade} margin={{ top: 4, right: 4, left: -16, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke={GRID_STROKE} vertical={false} />
              <XAxis dataKey="name" tick={AXIS_TICK} axisLine={false} tickLine={false} />
              <YAxis tick={AXIS_TICK} axisLine={false} tickLine={false} />
              <Tooltip
                content={({ active, payload, label }) => (
                  <ChartTooltip
                    active={active}
                    payload={payload}
                    label={label}
                    valueLabel=""
                    formatValue={(value) => formatCurrency(value)}
                  />
                )}
                cursor={{ fill: BRAND.accentMuted, radius: 8 }}
              />
              <Bar dataKey="value" radius={[10, 10, 4, 4]} maxBarSize={52} {...BAR_ANIMATION}>
                {charts.spendByGrade.map((entry, index) => (
                  <Cell key={index} fill={entry.fill ?? CHART_COLORS[index % CHART_COLORS.length]} />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        ) : (
          <EmptyChart message="No grade data." />
        )}
      </ChartPanel>

      <ChartPanel title="By station" subtitle="Where this user fills up most">
        {charts.spendByStation.length > 0 ? (
          <ResponsiveContainer width="100%" height="100%">
            <BarChart
              data={charts.spendByStation}
              layout="vertical"
              margin={{ top: 4, right: 12, left: 4, bottom: 0 }}
            >
              <ChartGradientDefs />
              <CartesianGrid strokeDasharray="3 3" stroke={GRID_STROKE} horizontal={false} />
              <XAxis type="number" hide />
              <YAxis
                type="category"
                dataKey="name"
                width={96}
                tick={CATEGORY_TICK}
                axisLine={false}
                tickLine={false}
              />
              <Tooltip
                content={({ active, payload, label }) => (
                  <ChartTooltip
                    active={active}
                    payload={payload}
                    label={label}
                    valueLabel=""
                    formatValue={(value) => formatCurrency(value)}
                    accentColor={BRAND.foreground}
                  />
                )}
                cursor={{ fill: BRAND.accentMuted, radius: 8 }}
              />
              <Bar
                dataKey="value"
                radius={[6, 10, 10, 6]}
                maxBarSize={22}
                fill="url(#gradForestX)"
                {...BAR_ANIMATION}
              />
            </BarChart>
          </ResponsiveContainer>
        ) : (
          <EmptyChart message="No station breakdown." />
        )}
      </ChartPanel>
    </div>
  );
}
